import { useCallback, useEffect, useMemo, useState } from 'react';

import DeleteIcon from '@/assets/all-icons/delete-icon';
import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import ArtistAiProfileService from '@/services/artistAiProfile.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import GoBackButton from '@/shared-components/buttons/components/GoBackButton';
import ArtworkMultipleInput from '@/shared-components/forms/add-artwork/about/components/ArtworkMultipleInput';
import InfoIcon from '@/shared-components/icons/components/public/InfoIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import TextInput from '@/shared-components/inputs/components/new/TextInput';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import Loader from '@/shared-components/loader/Loader';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import './styles.css';

const TONE_OPTIONS = [
  { value: 'studio_guide', label: 'Studio Guide' },
  { value: 'gallerist', label: 'Gallerist' },
  { value: 'collector', label: 'Collector' },
  { value: 'poetic', label: 'Poetic' },
  { value: 'academic', label: 'Academic' },
];

const READING_OPTIONS = [
  { value: 'simple', label: 'Simple' },
  { value: 'standard', label: 'Standard' },
  { value: 'expert', label: 'Expert' },
  { value: 'SEA', label: 'SEA' },
];

const INFO_KEY_TO_CANONICAL = {
  assistant_name: 'assistant_name',
  default_message: 'default_message',
  tone: 'tone',
  reading: 'reading',
  interpretive: 'interpretive',
  bio_short: 'bio_short',
  vision: 'vision',
  origin_story: 'origin_story',
  distinctiveness: 'distinctiveness',
  themes: 'themes',
  materials_techniques: 'materials_techniques',
  palette_texture_feel: 'palette_texture_feel',
  motifs: 'motifs',
  artist_3_words: 'artist_3_words',
  art_3_words: 'art_3_words',
  inspirations: 'inspirations',
  career_highlights: 'career_highlights',
};

const RECURRENT_CANONICAL_KEYS = [
  'recurrent_themes',
  'recurrent_feelings',
  'recurrent_mediums',
  'recurrent_colors',
];

function renderInfoBlocks(blocks) {
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  return (
    <div className="artist-ai-setup-profile__info-popup">
      {safeBlocks.map((block, index) => {
        if (!block || typeof block !== 'object') return null;
        const key = `info-block-${index}`;
        const blockType = block.type || 'paragraph';
        const paragraphStyle = { margin: 0 };

        if (blockType === 'list') {
          const items = Array.isArray(block.items) ? block.items : [];
          return (
            <div key={key} className="artist-ai-setup-profile__info-popup-list">
              {items.map((item, itemIndex) => (
                <div key={`${key}-${itemIndex}`} className="artist-ai-setup-profile__info-popup-item">
                  <span className="artist-ai-setup-profile__info-popup-tone">{item?.label || ''}</span>
                  <span className="artist-ai-setup-profile__info-popup-dash">—</span>
                  <span>{item?.text || ''}</span>
                </div>
              ))}
            </div>
          );
        }

        if (blockType === 'limit') {
          return (
            <p key={key} className="artist-ai-setup-profile__info-popup-limit" style={paragraphStyle}>
              {block.text || ''}
            </p>
          );
        }

        if (blockType === 'example') {
          return (
            <div key={key} className="artist-ai-setup-profile__info-popup-examples">
              <p style={paragraphStyle}>{block.text || ''}</p>
            </div>
          );
        }

        return <p key={key} style={paragraphStyle}>{block.text || ''}</p>;
      })}
    </div>
  );
}

function infoBlocksToPlainText(blocks) {
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  return safeBlocks
    .map((block) => {
      if (!block || typeof block !== 'object') return '';
      const blockType = block.type || 'paragraph';
      if (blockType === 'list') {
        const items = Array.isArray(block.items) ? block.items : [];
        return items
          .map((item) => `${item?.label || ''}: ${item?.text || ''}`.trim())
          .filter(Boolean)
          .join('\n');
      }
      return String(block.text || '').trim();
    })
    .filter(Boolean)
    .join('\n\n');
}

function emptyTheme() {
  return { tag: '', meaning: '' };
}

function emptyMotif() {
  return { motif: '', how_to_find: '' };
}

function normalizeThemeRows(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  return safeRows
    .map((row) => ({
      tag: String(row?.tag || '').trim(),
      meaning: String(row?.meaning || '').trim(),
    }))
    .filter((row) => row.tag);
}

function normalizeMotifRows(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  return safeRows
    .map((row) => ({
      motif: String(row?.motif || '').trim(),
      how_to_find: String(row?.how_to_find || '').trim(),
    }))
    .filter((row) => row.motif);
}

function normalizeHighlights(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  return safeRows
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .slice(0, 15);
}

function buildFormFromProfile(rawProfile) {
  const profile = rawProfile || {};
  const chat = profile.chat_preferences || {};
  const context = profile.ai_context || {};

  const themes = Array.isArray(context.themes) && context.themes.length
    ? context.themes.map((row) => ({ tag: row?.tag || '', meaning: row?.meaning || '' }))
    : [emptyTheme(), emptyTheme(), emptyTheme()];

  const motifs = Array.isArray(context.motifs) && context.motifs.length
    ? context.motifs.map((row) => ({ motif: row?.motif || '', how_to_find: row?.how_to_find || '' }))
    : [emptyMotif(), emptyMotif(), emptyMotif()];

  const career = Array.isArray(context.career_highlights) && context.career_highlights.length
    ? context.career_highlights.map((item) => String(item || ''))
    : ['', '', ''];

  return {
    recurrent: profile.recurrent !== false,
    recurrent_themes: Array.isArray(profile.recurrent_themes) ? profile.recurrent_themes : [],
    recurrent_feelings: Array.isArray(profile.recurrent_feelings) ? profile.recurrent_feelings : [],
    recurrent_mediums: Array.isArray(profile.recurrent_mediums) ? profile.recurrent_mediums : [],
    recurrent_colors: Array.isArray(profile.recurrent_colors) ? profile.recurrent_colors : [],
    chat_preferences: {
      assistant_name: chat.assistant_name || 'SEA',
      default_message: chat.default_message || '',
      tone: chat.tone || 'studio_guide',
      reading: chat.reading || 'standard',
      allow_interpretive_possibilities: chat.allow_interpretive_possibilities !== false,
    },
    ai_context: {
      bio_short: context.bio_short || '',
      vision: context.vision || '',
      origin_story: context.origin_story || '',
      distinctiveness: context.distinctiveness || '',
      themes,
      materials_techniques: context.materials_techniques || '',
      palette_texture_feel: context.palette_texture_feel || '',
      motifs,
      artist_3_words: context.artist_3_words || '',
      art_3_words: context.art_3_words || '',
      inspirations: context.inspirations || '',
      career_highlights: career,
    },
  };
}

function buildPayload(form) {
  return {
    recurrent: Boolean(form.recurrent),
    recurrent_themes: (form.recurrent_themes || []).slice(0, 3),
    recurrent_feelings: (form.recurrent_feelings || []).slice(0, 3),
    recurrent_mediums: (form.recurrent_mediums || []).slice(0, 3),
    recurrent_colors: (form.recurrent_colors || []).slice(0, 3),
    chat_preferences: {
      assistant_name: String(form.chat_preferences?.assistant_name || 'SEA').trim() || 'SEA',
      default_message: String(form.chat_preferences?.default_message || '').trim() || null,
      tone: form.chat_preferences?.tone || 'studio_guide',
      reading: form.chat_preferences?.reading || 'standard',
      allow_interpretive_possibilities: form.chat_preferences?.allow_interpretive_possibilities !== false,
    },
    ai_context: {
      bio_short: String(form.ai_context?.bio_short || '').trim() || null,
      vision: String(form.ai_context?.vision || '').trim() || null,
      origin_story: String(form.ai_context?.origin_story || '').trim() || null,
      distinctiveness: String(form.ai_context?.distinctiveness || '').trim() || null,
      themes: normalizeThemeRows(form.ai_context?.themes || []),
      materials_techniques: String(form.ai_context?.materials_techniques || '').trim() || null,
      palette_texture_feel: String(form.ai_context?.palette_texture_feel || '').trim() || null,
      motifs: normalizeMotifRows(form.ai_context?.motifs || []),
      artist_3_words: String(form.ai_context?.artist_3_words || '').trim() || null,
      art_3_words: String(form.ai_context?.art_3_words || '').trim() || null,
      inspirations: String(form.ai_context?.inspirations || '').trim() || null,
      career_highlights: normalizeHighlights(form.ai_context?.career_highlights || []),
    },
  };
}

function normalizeRecurrentDefaults(rawProfile) {
  const defaults = rawProfile?.recurrent_defaults || {};

  const themes = Array.isArray(defaults.themes)
    ? defaults.themes.slice(0, 3)
    : (Array.isArray(rawProfile?.recurrent_themes) ? rawProfile.recurrent_themes.slice(0, 3) : []);
  const feelings = Array.isArray(defaults.feelings)
    ? defaults.feelings.slice(0, 3)
    : (Array.isArray(rawProfile?.recurrent_feelings) ? rawProfile.recurrent_feelings.slice(0, 3) : []);
  const mediums = Array.isArray(defaults.mediums)
    ? defaults.mediums.slice(0, 3)
    : (Array.isArray(rawProfile?.recurrent_mediums) ? rawProfile.recurrent_mediums.slice(0, 3) : []);
  const colors = Array.isArray(defaults.colors)
    ? defaults.colors.slice(0, 3)
    : (Array.isArray(rawProfile?.recurrent_colors) ? rawProfile.recurrent_colors.slice(0, 3) : []);

  return {
    themes,
    feelings,
    mediums,
    colors,
  };
}

function FieldTitle({ label, infoKey, setOpenInfoKey }) {
  return (
    <div className="artist-ai-setup-profile__field-title">
      <h4>{label}</h4>
      {infoKey ? (
        <InfoIcon
          className="artist-ai-setup-profile__info-icon"
          onClick={(event) => {
            event.stopPropagation();
            setOpenInfoKey((current) => (
              current?.key === infoKey ? null : { key: infoKey, label }
            ));
          }}
        />
      ) : null}
    </div>
  );
}

export default function ProfileAiSetup() {
  const { stateHandler } = useStateHandler();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [openInfoKey, setOpenInfoKey] = useState(null);
  const [selectedGalleryArtistId, setSelectedGalleryArtistId] = useState(null);
  const [scope, setScope] = useState({ selected: null, representations: [] });
  const [form, setForm] = useState(() => buildFormFromProfile(null));
  const [recurrentDefaults, setRecurrentDefaults] = useState(() => normalizeRecurrentDefaults(null));
  const [infoContentItems, setInfoContentItems] = useState({});

  const infoPopupContent = useMemo(() => {
    if (!openInfoKey?.key) {
      return null;
    }

    if (openInfoKey.key === 'recurrent') {
      const blocks = RECURRENT_CANONICAL_KEYS
        .map((key) => {
          const item = infoContentItems[key];
          const firstParagraph = item?.blocks?.find((block) => (block?.type || 'paragraph') === 'paragraph');
          if (!item || !firstParagraph?.text) {
            return null;
          }
          return {
            type: 'paragraph',
            text: `${item.title}: ${firstParagraph.text}`,
          };
        })
        .filter(Boolean);

      return {
        title: 'Recurrent',
        text: renderInfoBlocks(blocks),
      };
    }

    const canonicalKey = INFO_KEY_TO_CANONICAL[openInfoKey.key];
    const item = canonicalKey ? infoContentItems[canonicalKey] : null;
    if (!item) {
      return {
        title: openInfoKey?.label || 'Information',
        text: <p>No additional information available.</p>,
      };
    }
    return {
      title: item.title || openInfoKey?.label || 'Information',
      text: renderInfoBlocks(item.blocks),
    };
  }, [infoContentItems, openInfoKey]);

  const loadProfile = useCallback(async (galleryArtistId = null) => {
    setLoading(true);
    setError('');
    try {
      const { response, data } = await ArtistAiProfileService.getProfile(galleryArtistId);
      if (!response?.ok) {
        setError(data?.message || 'Unable to load AI setup.');
        return;
      }

      const profile = data?.profile || data || {};
      const nextScope = data?.scope || { selected: null, representations: [] };

      setScope(nextScope);
      setSelectedGalleryArtistId(
        nextScope?.selected?.gallery_artist_id
          || galleryArtistId
          || null
      );
      setForm(buildFormFromProfile(profile));
      setRecurrentDefaults(normalizeRecurrentDefaults(profile));
    } catch (loadError) {
      setError('Unable to load AI setup.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    let isSubscribed = true;
    (async () => {
      try {
        const { response, data } = await ArtistAiProfileService.getInfoContent();
        if (!response?.ok || !isSubscribed) {
          return;
        }
        if (data?.items && typeof data.items === 'object') {
          setInfoContentItems(data.items);
        }
      } catch (contentError) {
        // Keep silent fallback: popup will show generic message if content is unavailable.
      }
    })();

    return () => {
      isSubscribed = false;
    };
  }, []);

  function toggleInfoPopup(infoKey, label) {
    setOpenInfoKey((current) => (
      current?.key === infoKey ? null : { key: infoKey, label }
    ));
  }

  function renderLabelWithInfo(labelText, infoKey, popupLabel = labelText) {
    return (
      <span className="artist-ai-setup-profile__floating-label">
        <span>{labelText}</span>
        <InfoIcon
          className="artist-ai-setup-profile__info-icon artist-ai-setup-profile__info-icon--inline-label"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleInfoPopup(infoKey, popupLabel);
          }}
        />
      </span>
    );
  }

  const getInfoDescription = useCallback((infoKey) => {
    const canonicalKey = INFO_KEY_TO_CANONICAL[infoKey];
    const item = canonicalKey ? infoContentItems[canonicalKey] : null;
    if (!item) {
      return 'No additional information available.';
    }
    const text = infoBlocksToPlainText(item.blocks);
    return text || 'No additional information available.';
  }, [infoContentItems]);

  function updateChatPreference(field, value) {
    setForm((current) => ({
      ...current,
      chat_preferences: {
        ...current.chat_preferences,
        [field]: value,
      },
    }));
  }

  function updateAiContext(field, value) {
    setForm((current) => ({
      ...current,
      ai_context: {
        ...current.ai_context,
        [field]: value,
      },
    }));
  }

  function updateThemeRow(index, field, value) {
    setForm((current) => {
      const nextRows = [...(current.ai_context.themes || [])];
      nextRows[index] = {
        ...(nextRows[index] || emptyTheme()),
        [field]: value,
      };
      return {
        ...current,
        ai_context: {
          ...current.ai_context,
          themes: nextRows,
        },
      };
    });
  }

  function updateMotifRow(index, field, value) {
    setForm((current) => {
      const nextRows = [...(current.ai_context.motifs || [])];
      nextRows[index] = {
        ...(nextRows[index] || emptyMotif()),
        [field]: value,
      };
      return {
        ...current,
        ai_context: {
          ...current.ai_context,
          motifs: nextRows,
        },
      };
    });
  }

  function updateCareerHighlight(index, value) {
    setForm((current) => {
      const nextRows = [...(current.ai_context.career_highlights || [])];
      nextRows[index] = value;
      return {
        ...current,
        ai_context: {
          ...current.ai_context,
          career_highlights: nextRows,
        },
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload(form);
      const { response, data } = await ArtistAiProfileService.upsertProfile(payload, selectedGalleryArtistId);
      if (!response?.ok) {
        setError(data?.message || 'Unable to save AI setup.');
        return;
      }

      const profile = data?.profile || data || {};
      const nextScope = data?.scope || scope;

      setScope(nextScope);
      setSelectedGalleryArtistId(nextScope?.selected?.gallery_artist_id || selectedGalleryArtistId);
      setForm(buildFormFromProfile(profile));
      setRecurrentDefaults(normalizeRecurrentDefaults(profile));
      stateHandler.set('temporalPopup', {
        text: 'AI setup saved and synchronized.',
      });
    } catch (saveError) {
      setError('Unable to save AI setup.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="artist-ai-setup-profile">
      <header className="artist-ai-setup-profile__header">
        <GoBackButton alternativeRoute="/profile" />
        <h1>AI Set up</h1>
        <ContinueButton className="artist-ai-setup-profile__save-button" onClick={handleSave} loading={saving}>
          Save
        </ContinueButton>
      </header>

      {loading ? <Loader thin className="artist-ai-setup-profile__loader" /> : null}
      {!loading && error ? <p className="artist-ai-setup-profile__error">{error}</p> : null}

      {!loading && (
        <div className="artist-ai-setup-profile__body">
          {Array.isArray(scope?.representations) && scope.representations.length > 1 ? (
            <div className="artist-ai-setup-profile__panel">
              <FieldTitle
                label="Representation"
                setOpenInfoKey={setOpenInfoKey}
              />
              <p className="artist-ai-setup-profile__panel-caption">
                  Select the represented profile to edit.
              </p>
              <select
                className="artist-ai-setup-profile__select"
                value={selectedGalleryArtistId || ''}
                onChange={(event) => {
                  const nextGalleryArtistId = event.target.value;
                  setSelectedGalleryArtistId(nextGalleryArtistId);
                  loadProfile(nextGalleryArtistId);
                }}
              >
                {scope.representations.map((item) => (
                  <option key={item.gallery_artist_id} value={item.gallery_artist_id}>
                    {item.artist_name || 'Artist'} · {item.gallery_id}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="artist-ai-setup-profile__main-layout">
            <div className="artist-ai-setup-profile__panel artist-ai-setup-profile__panel--grouped artist-ai-setup-profile__panel--chat">
              <FieldTitle
                label="AI Chat Preferences"
                setOpenInfoKey={setOpenInfoKey}
              />
              <TextInput
                label={(
                  <span className="artist-ai-setup-profile__floating-label">
                    <span>Assistant name</span>
                    <InfoIcon
                      className="artist-ai-setup-profile__info-icon artist-ai-setup-profile__info-icon--inline-label"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleInfoPopup('assistant_name', 'Assistant name');
                      }}
                    />
                  </span>
                )}
                value={form.chat_preferences.assistant_name}
                onChange={(event) => updateChatPreference('assistant_name', event.target.value)}
              />

              <PublicFormInput
                type="textarea"
                placeholder="Default message (max 100)"
                element="ai_chat_default_message"
                infoIconDescription={getInfoDescription('default_message')}
                maxLength={100}
                value={form.chat_preferences.default_message}
                onChange={(event) => updateChatPreference('default_message', event.target.value)}
              />

              <div className="artist-ai-setup-profile__select-grid">
                <div>
                  <PublicFormInput
                    type="select"
                    placeholder="Tone"
                    element="ai_chat_tone"
                    infoIconDescription={getInfoDescription('tone')}
                    value={form.chat_preferences.tone}
                    onChange={(value) => updateChatPreference('tone', value)}
                    selectOptions={{ options: TONE_OPTIONS }}
                  />
                </div>
                <div>
                  <PublicFormInput
                    type="select"
                    placeholder="Reading"
                    element="ai_chat_reading"
                    infoIconDescription={getInfoDescription('reading')}
                    value={form.chat_preferences.reading}
                    onChange={(value) => updateChatPreference('reading', value)}
                    selectOptions={{ options: READING_OPTIONS }}
                  />
                </div>
              </div>

              <div className="artist-ai-setup-profile__toggle-row">
                <div className="artist-ai-setup-profile__toggle-main">
                  <label htmlFor="interpretive-toggle">Allow interpretive possibilities</label>
                  <InfoIcon
                    className="artist-ai-setup-profile__info-icon artist-ai-setup-profile__info-icon--toggle"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleInfoPopup('interpretive', 'Allow interpretive possibilities');
                    }}
                  />
                </div>
                <input
                  id="interpretive-toggle"
                  type="checkbox"
                  checked={Boolean(form.chat_preferences.allow_interpretive_possibilities)}
                  onChange={(event) => updateChatPreference('allow_interpretive_possibilities', event.target.checked)}
                />
              </div>
            </div>

            <div className="artist-ai-setup-profile__panel artist-ai-setup-profile__panel--grouped">
              <FieldTitle
                label="Recurrent"
                infoKey="recurrent"
                setOpenInfoKey={setOpenInfoKey}
              />
              <div className="artist-ai-setup-profile__toggle-row">
                <div className="artist-ai-setup-profile__toggle-main">
                  <label htmlFor="recurrent-toggle">Use recurrent defaults</label>
                </div>
                <input
                  id="recurrent-toggle"
                  type="checkbox"
                  checked={Boolean(form.recurrent)}
                  onChange={(event) =>
                    setForm((current) => {
                      const shouldUseDefaults = event.target.checked;
                      if (!shouldUseDefaults) {
                        return {
                          ...current,
                          recurrent: false,
                        };
                      }
                      return {
                        ...current,
                        recurrent: true,
                        recurrent_themes: recurrentDefaults.themes,
                        recurrent_feelings: recurrentDefaults.feelings,
                        recurrent_mediums: recurrentDefaults.mediums,
                        recurrent_colors: recurrentDefaults.colors,
                      };
                    })}
                />
              </div>

              <div className="artist-ai-setup-profile__recurrent-options">
                <ArtworkMultipleInput
                  placeholder="Theme(s)"
                  type="themes"
                  options={config?.forms?.theme_options || []}
                  value={form.recurrent_themes || []}
                  element="recurrent_themes"
                  onChange={(nextValue) =>
                    setForm((current) => ({
                      ...current,
                      recurrent: false,
                      recurrent_themes: nextValue.slice(0, 3),
                    }))}
                  previewMode={false}
                  margin
                />
                <ArtworkMultipleInput
                  placeholder="Emotion(s)"
                  type="emotions"
                  options={config?.forms?.feeling_options || []}
                  value={form.recurrent_feelings || []}
                  element="recurrent_feelings"
                  onChange={(nextValue) =>
                    setForm((current) => ({
                      ...current,
                      recurrent: false,
                      recurrent_feelings: nextValue.slice(0, 3),
                    }))}
                  previewMode={false}
                  margin
                />
                <ArtworkMultipleInput
                  placeholder="Medium(s)"
                  type="mediums"
                  options={config?.forms?.medium_options || []}
                  value={form.recurrent_mediums || []}
                  element="recurrent_mediums"
                  onChange={(nextValue) =>
                    setForm((current) => ({
                      ...current,
                      recurrent: false,
                      recurrent_mediums: nextValue.slice(0, 3),
                    }))}
                  previewMode={false}
                  margin
                />
                <ArtworkMultipleInput
                  placeholder="Color(s)"
                  type="colors"
                  options={config?.forms?.color_options || []}
                  value={form.recurrent_colors || []}
                  element="recurrent_colors"
                  onChange={(nextValue) =>
                    setForm((current) => ({
                      ...current,
                      recurrent: false,
                      recurrent_colors: nextValue.slice(0, 3),
                    }))}
                  previewMode={false}
                  margin
                />
              </div>
            </div>

            <div className="artist-ai-setup-profile__panel artist-ai-setup-profile__panel--wide artist-ai-setup-profile__panel--grouped artist-ai-setup-profile__panel--context">
              <FieldTitle
                label="AI Context"
                setOpenInfoKey={setOpenInfoKey}
              />

              <PublicFormInput
                type="textarea"
                placeholder="Who is the artist (short bio)"
                element="ai_context_bio_short"
                infoIconDescription={getInfoDescription('bio_short')}
                maxLength={700}
                value={form.ai_context.bio_short}
                onChange={(event) => updateAiContext('bio_short', event.target.value)}
              />
              <PublicFormInput
                type="textarea"
                placeholder="What is the artist vision"
                element="ai_context_vision"
                infoIconDescription={getInfoDescription('vision')}
                maxLength={600}
                value={form.ai_context.vision}
                onChange={(event) => updateAiContext('vision', event.target.value)}
              />
              <PublicFormInput
                type="textarea"
                placeholder="Origin story"
                element="ai_context_origin_story"
                infoIconDescription={getInfoDescription('origin_story')}
                maxLength={600}
                value={form.ai_context.origin_story}
                onChange={(event) => updateAiContext('origin_story', event.target.value)}
              />
              <PublicFormInput
                type="textarea"
                placeholder="What makes the artist distinct"
                element="ai_context_distinctiveness"
                infoIconDescription={getInfoDescription('distinctiveness')}
                maxLength={600}
                value={form.ai_context.distinctiveness}
                onChange={(event) => updateAiContext('distinctiveness', event.target.value)}
              />

              <div className="artist-ai-setup-profile__subsection">
                <FieldTitle
                  label="Core themes explored"
                  infoKey="themes"
                  setOpenInfoKey={setOpenInfoKey}
                />
                {(form.ai_context.themes || []).map((row, index) => (
                  <div
                    className={`artist-ai-setup-profile__row${index >= 3 ? ' artist-ai-setup-profile__row--removable' : ''}`}
                    key={`theme-${index}`}
                  >
                    <TextInput
                      label={`Theme ${index + 1}`}
                      value={row.tag}
                      onChange={(event) => updateThemeRow(index, 'tag', event.target.value)}
                    />
                    <PublicFormInput
                      type="textarea"
                      placeholder="Meaning"
                      element={`ai_context_theme_meaning_${index}`}
                      maxLength={160}
                      value={row.meaning}
                      onChange={(event) => updateThemeRow(index, 'meaning', event.target.value)}
                    />
                    {index >= 3 ? (
                      <button
                        type="button"
                        className="artist-ai-setup-profile__remove"
                        aria-label={`Remove theme ${index + 1}`}
                        onClick={() => {
                          setForm((current) => ({
                            ...current,
                            ai_context: {
                              ...current.ai_context,
                              themes: current.ai_context.themes.filter((_, rowIndex) => rowIndex !== index),
                            },
                          }));
                        }}
                      >
                        <DeleteIcon className="artist-ai-setup-profile__remove-icon" />
                      </button>
                    ) : null}
                  </div>
                ))}
                <ContinueButton
                  className="artist-ai-setup-profile__inline-button"
                  onClick={() => {
                    setForm((current) => ({
                      ...current,
                      ai_context: {
                        ...current.ai_context,
                        themes: [...(current.ai_context.themes || []), emptyTheme()],
                      },
                    }));
                  }}
                >
                  Add Theme
                </ContinueButton>
              </div>

              <PublicFormInput
                type="textarea"
                placeholder="Materials & techniques"
                element="ai_context_materials_techniques"
                infoIconDescription={getInfoDescription('materials_techniques')}
                maxLength={700}
                value={form.ai_context.materials_techniques}
                onChange={(event) => updateAiContext('materials_techniques', event.target.value)}
              />
              <PublicFormInput
                type="textarea"
                placeholder="Palette, texture, and feel"
                element="ai_context_palette_texture_feel"
                infoIconDescription={getInfoDescription('palette_texture_feel')}
                maxLength={600}
                value={form.ai_context.palette_texture_feel}
                onChange={(event) => updateAiContext('palette_texture_feel', event.target.value)}
              />

              <div className="artist-ai-setup-profile__subsection">
                <FieldTitle
                  label="Recurring motifs"
                  infoKey="motifs"
                  setOpenInfoKey={setOpenInfoKey}
                />
                {(form.ai_context.motifs || []).map((row, index) => (
                  <div
                    className={`artist-ai-setup-profile__row${index >= 3 ? ' artist-ai-setup-profile__row--removable' : ''}`}
                    key={`motif-${index}`}
                  >
                    <TextInput
                      label={`Motif ${index + 1}`}
                      value={row.motif}
                      onChange={(event) => updateMotifRow(index, 'motif', event.target.value)}
                    />
                    <PublicFormInput
                      type="textarea"
                      placeholder="How to find"
                      element={`ai_context_motif_how_to_find_${index}`}
                      maxLength={160}
                      value={row.how_to_find}
                      onChange={(event) => updateMotifRow(index, 'how_to_find', event.target.value)}
                    />
                    {index >= 3 ? (
                      <button
                        type="button"
                        className="artist-ai-setup-profile__remove"
                        aria-label={`Remove motif ${index + 1}`}
                        onClick={() => {
                          setForm((current) => ({
                            ...current,
                            ai_context: {
                              ...current.ai_context,
                              motifs: current.ai_context.motifs.filter((_, rowIndex) => rowIndex !== index),
                            },
                          }));
                        }}
                      >
                        <DeleteIcon className="artist-ai-setup-profile__remove-icon" />
                      </button>
                    ) : null}
                  </div>
                ))}
                <ContinueButton
                  className="artist-ai-setup-profile__inline-button"
                  onClick={() => {
                    setForm((current) => ({
                      ...current,
                      ai_context: {
                        ...current.ai_context,
                        motifs: [...(current.ai_context.motifs || []), emptyMotif()],
                      },
                    }));
                  }}
                >
                  Add Motif
                </ContinueButton>
              </div>

              <TextInput
                label={renderLabelWithInfo('Describe the artist in 3 words', 'artist_3_words')}
                maxLength={60}
                value={form.ai_context.artist_3_words}
                onChange={(event) => updateAiContext('artist_3_words', event.target.value)}
              />
              <TextInput
                label={renderLabelWithInfo('Describe the art in 3 words', 'art_3_words')}
                maxLength={60}
                value={form.ai_context.art_3_words}
                onChange={(event) => updateAiContext('art_3_words', event.target.value)}
              />
              <PublicFormInput
                type="textarea"
                placeholder="Inspirations and references"
                element="ai_context_inspirations"
                infoIconDescription={getInfoDescription('inspirations')}
                maxLength={600}
                value={form.ai_context.inspirations}
                onChange={(event) => updateAiContext('inspirations', event.target.value)}
              />

              <div className="artist-ai-setup-profile__subsection">
                <FieldTitle
                  label="Career highlights"
                  infoKey="career_highlights"
                  setOpenInfoKey={setOpenInfoKey}
                />
                {(form.ai_context.career_highlights || []).map((value, index) => (
                  <div
                    className={`artist-ai-setup-profile__row artist-ai-setup-profile__row--single${index >= 3 ? ' artist-ai-setup-profile__row--removable' : ''}`}
                    key={`career-${index}`}
                  >
                    <TextInput
                      label={`Highlight ${index + 1}`}
                      maxLength={90}
                      value={value}
                      onChange={(event) => updateCareerHighlight(index, event.target.value)}
                    />
                    {index >= 3 ? (
                      <button
                        type="button"
                        className="artist-ai-setup-profile__remove"
                        aria-label={`Remove highlight ${index + 1}`}
                        onClick={() => {
                          setForm((current) => ({
                            ...current,
                            ai_context: {
                              ...current.ai_context,
                              career_highlights: current.ai_context.career_highlights.filter((_, rowIndex) => rowIndex !== index),
                            },
                          }));
                        }}
                      >
                        <DeleteIcon className="artist-ai-setup-profile__remove-icon" />
                      </button>
                    ) : null}
                  </div>
                ))}
                <ContinueButton
                  className="artist-ai-setup-profile__inline-button"
                  onClick={() => {
                    setForm((current) => ({
                      ...current,
                      ai_context: {
                        ...current.ai_context,
                        career_highlights: [...(current.ai_context.career_highlights || []), ''],
                      },
                    }));
                  }}
                >
                  Add Highlight
                </ContinueButton>
              </div>
            </div>
          </div>

        </div>
      )}

      <GenericPopup
        open={Boolean(infoPopupContent)}
        onBackgroundClick={() => setOpenInfoKey(null)}
        className="generic-popup_content remove-scrollbar artist-ai-setup-profile__info-popup-card"
        opacity
      >
        <div className="generic-popup__header">
          <XIcon
            className="generic-popup__close"
            onClick={(event) => {
              event.stopPropagation();
              setOpenInfoKey(null);
            }}
          />
          <Text className="generic-popup__header-text-theme">
            {infoPopupContent?.title || openInfoKey?.label || 'Information'}
          </Text>
        </div>

        {infoPopupContent?.text || (
          <p style={{ marginBottom: 0 }}>No additional information available.</p>
        )}
      </GenericPopup>
    </section>
  );
}
